import type { FC } from "react";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import { Button, Modal } from "../../../shared";

import styles from "./ConfirmDeleteChallengeModal.module.css";

export type ConfirmDeleteChallengeModalProps = {
  open: boolean;
  /** Name of the challenge being deleted — surfaced inline in the body copy. */
  challengeName: string;
  onCancel: () => void;
  /** Fired only after the destructive CTA is clicked. The parent is
   *  responsible for actually removing the challenge and closing the modal. */
  onConfirm: () => void;
  /** When `true`, the destructive CTA shows the standard button loader and
   *  both actions are disabled while the delete request is in flight. */
  isDeleting?: boolean;
};

/**
 * Confirm-before-destroy modal shown when the admin clicks the per-card
 * `Delete` button on a saved challenge. Matches the destructive popup in
 * the Game Pass Figma: red title, two outlined buttons (Go Back / Delete).
 */
const ConfirmDeleteChallengeModal: FC<ConfirmDeleteChallengeModalProps> = ({
  open,
  challengeName,
  onCancel,
  onConfirm,
  isDeleting = false,
}) => (
  <Modal
    open={open}
    onClose={onCancel}
    intent="destructive"
    size="sm"
    // Unmount on close so the next open starts with a clean DOM (no stale
    // body, no leaked focus traps) and tests can assert by presence.
    destroyOnHidden
    title={
      <span className={styles.title}>
        {AppStrings.DELETE_CHALLENGE_MODAL_TITLE}
      </span>
    }
    footer={
      <div className={styles.footer}>
        <Button
          intent="neutral"
          variant="outline"
          onClick={onCancel}
          disabled={isDeleting}
          data-testid="challenge-card-confirm-delete-go-back"
        >
          {AppStrings.DELETE_CHALLENGE_GO_BACK}
        </Button>
        <Button
          intent="danger"
          variant="outline"
          // Outline variant defaults to white text (via
          // `--COLOR-LIGHT_TEXT_COLOR`); for the destructive popup the
          // Figma uses red text matching the border, so the colour is
          // applied via a CSS module class.
          className={styles.deleteButton}
          onClick={onConfirm}
          loading={isDeleting}
          data-testid="challenge-card-confirm-delete-confirm"
        >
          {AppStrings.DELETE_CHALLENGE_CONFIRM_CTA}
        </Button>
      </div>
    }
  >
    <div data-testid="challenge-card-confirm-delete-modal">
      <p className={styles.body}>
        {`${AppStrings.DELETE_CHALLENGE_CONFIRM_PREFIX} `}
        <span className={styles.bodyName}>{`"${challengeName}"`}</span>
      </p>
    </div>
  </Modal>
);

export default ConfirmDeleteChallengeModal;
